import { Injectable } from '@angular/core';

import { Chat } from '../../bayadic-shared-models/chat/Chat';
import { LocalNotificationManagerProvider } from '../local-notification-manager/local-notification-manager';
import { LeitnerMessage } from '../../bayadic-shared-models/classes/message/LeitnerMessage';
import { ChatsDatabaseService } from '../chats-database-service/chats-database-service';
import { SocketManagerProvider } from '../socket-manager/socket-manager';
import { ACTIONS } from '../../bayadic-shared-models/enums/Enums';
import { UpdateMessageRequest } from '../../bayadic-shared-models/item/update-message-request';
import { UpdateMessageResponse } from '../../bayadic-shared-models/item/update-message-response';
import { createEmptyCard, fsrs, generatorParameters, Rating } from 'ts-fsrs';
import { LoggerService } from '../../app/services/logger.service';
type Difficulty = number;
type Stability = number;
type Interval = number;
@Injectable()
export class SpaceRepetitionManagerProvider {

    constructor(
        public chatDbService: ChatsDatabaseService,
        public localNotificationMgr: LocalNotificationManagerProvider,
        public socketManager: SocketManagerProvider,
        public logger: LoggerService
    ) { }
    async easy(message: LeitnerMessage, chat: Chat) {
        message.scheduledTime = this.calculateWithFSRF(message, 5);
        await this.updateMessage(chat, message);

        // this.localNotificationMgr.updateLeitnerNotificationsSchedule();
    }
    private async updateMessage(chat: Chat, message: LeitnerMessage) {
        await this.chatDbService.updateMessageByChat(chat, message);
        await this.updateMessageCloud(chat, message);
        chat.messages.find((msg) => msg.messageID == message.messageID).scheduledTime = message.scheduledTime;
        this.logger.debug("message updated", {message})
    }

    private async updateMessageCloud(chat: Chat, message: LeitnerMessage) {
        await this.socketManager.asyncQuery<UpdateMessageResponse, UpdateMessageRequest>(ACTIONS.UPDATE_MESSAGE, {
            messageID: message.messageID, 
            message: {
                scheduledTime: message.scheduledTime,
                content: message.content,
                type: message.messageType
            }
        });
    }

    async medium(message: LeitnerMessage, chat: Chat) {
        message.scheduledTime = this.calculateWithFSRF(message, 3);
        this.logger.debug("message updated date", {message, "scheduledTime" : message.scheduledTime})

        await this.updateMessage(chat, message);
    }
    async hard(message: LeitnerMessage, chat: Chat) {
        message.scheduledTime = this.calculateWithFSRF(message, 1);
        await this.updateMessage(chat, message);

        // this.localNotificationMgr.updateLeitnerNotificationsSchedule();
    }
    calculateWithFSRF(leitnerMessage: LeitnerMessage, quality: number): Date {
        const params = generatorParameters({enable_fuzz: true});

        // Adjusting the parameters
        params.request_retention = 1 ;  // Ensure it's at least 1.1
        params.maximum_interval = 1000  // Ensure minimum interval multiplier

        const fsrsInstance = fsrs(params);
        const now = new Date();
        const card = createEmptyCard();
        card.difficulty = leitnerMessage.content.difficulty;
        card.due = leitnerMessage.scheduledTime;
        card.elapsed_days = leitnerMessage.content.elapsedDays;
        card.lapses = leitnerMessage.content.lapses;
        card.last_review = leitnerMessage.content.lastReviewDate;
        card.reps = leitnerMessage.content.reps;
        card.scheduled_days = leitnerMessage.content.scheduledDays;
        card.stability = leitnerMessage.content.stability;
        card.state = leitnerMessage.content.state;
        const schedulingResult = fsrsInstance.repeat(card, now);
        let rating = Rating.Good;
        switch (quality) {
            case 1:
                rating = Rating.Hard;
                break;
            case 3:
                rating = Rating.Good;
                break;
            case 5:
                rating = Rating.Easy;
                break;
        }
        const scheduleingCard = schedulingResult[rating].card
        leitnerMessage.content.difficulty = scheduleingCard.difficulty;
        leitnerMessage.scheduledTime = scheduleingCard.due;
        leitnerMessage.content.elapsedDays = scheduleingCard.elapsed_days;
        leitnerMessage.content.lapses = scheduleingCard.lapses;
        leitnerMessage.content.lastReviewDate = scheduleingCard.last_review;
        leitnerMessage.content.reps = scheduleingCard.reps;
        leitnerMessage.content.scheduledDays = scheduleingCard.scheduled_days;
        leitnerMessage.content.stability = scheduleingCard.stability;
        leitnerMessage.content.state = scheduleingCard.state;
        return scheduleingCard.due;

    }

    calculateSuperMemo2Algorithm(card: LeitnerMessage, quality: number): Date {
        //SuperMemo algorithm
        //Here are some terms that we will deal with when impementing the SuperMemo (SM-2) algorithm of spaced repetition.
        //
        //repetitions - this is the number of times a user sees a flashcard. 0 means they haven't studied it yet, 1 means it is their first time, and so on. It is also referred to as n in some of the documentation.
        //quality - also known as quality of assessment. This is how difficult (as defined by the user) a flashcard is. The scale is from 0 to 5.
        //easiness - this is also referred to as the easiness factor or EFactor or EF. It is multiplier used to increase the "space" in spaced repetition. The range is from 1.3 to 2.5.
        //interval - this is the length of time (in days) between repetitions. It is the "space" of spaced repetition.
        //nextPractice - This is the date/time of when the flashcard comes due to review again.
        //Default values
        if (quality < 0 || quality > 5) {
            throw new Error("Quality should be within the range 0-5");
        }
        const easiness = card.getEasinessFactor();
        let repetitions = card.getRepetitions();
        let interval = card.getInterval();

        // Update easiness factor
        if (quality < 3) {
            card.content.easinessFactor = Math.max(1.3, easiness - 0.2);
        } else {
            card.content.easinessFactor = easiness + 0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02);
        }

        // Update repetitions
        repetitions = quality < 3 ? 0 : repetitions + 1;
        card.content.repetition = repetitions;

        // Calculate interval
        if (repetitions <= 1) {
            interval = 1;
        } else if (repetitions === 2) {
            interval = 6;
        } else {
            interval *= card.getEasinessFactor();
        }

        // card.getInterval = interval;

        // Calculate next practice date
        const today = new Date();
        const nextPracticeDate = new Date();
        nextPracticeDate.setDate(today.getDate() + interval);
        this.logger.debug("next pratice date", {nextPracticeDate})
        return nextPracticeDate;
    }

    getIntervalByRepetitions(repetitions: number, easiness: number): number {
        if (repetitions <= 1) {
            return 1;
        } else if (repetitions === 2) {
            return 6;
        } else {
            return Math.round(this.getIntervalByRepetitions(repetitions - 1, easiness) * easiness);
        }
    }

    calculateNextIntervalSM18(
        currentStability: Stability,
        difficulty: Difficulty,
        lastInterval: Interval
    ): Interval {
        // Adjust stability based on difficulty
        let adjustedStability = currentStability * (1 + difficulty - 0.5);
    
        // The next interval is typically a product of the adjusted stability and a multiplier
        // This multiplier can be based on historical performance or empirical data.
        let multiplier = 1.3; // A multiplier greater than 1 to space out reviews
        let nextInterval = adjustedStability * multiplier;
    
        // Ensure the next interval is greater than the last interval to prevent regression
        nextInterval = Math.max(nextInterval, lastInterval * 1.2);
    
        return Math.round(nextInterval);
    }
    
    getNextReviewDateSM18(currentDate: Date, nextInterval: Interval): Date {
        const nextReviewDate = new Date(currentDate);
        nextReviewDate.setDate(currentDate.getDate() + nextInterval);
        return nextReviewDate;
    }
    calculateSM18Algorithm2(leitnerMessage: LeitnerMessage, performanceRating: number): Date {
        const currentStability = leitnerMessage.content.stability;
        const currentEasinessFactor = leitnerMessage.content.easinessFactor;
        const lastReviewedTime = leitnerMessage.content.lastReviewDate;
    
        // Adjust the easiness factor based on the performance
        let newEasinessFactor;
        if (performanceRating === 5) { // Easy
          newEasinessFactor = currentEasinessFactor + 0.1;
        } else if (performanceRating === 3) { // Medium
          newEasinessFactor = currentEasinessFactor;
        } else if (performanceRating === 1) { // Hard
          newEasinessFactor = currentEasinessFactor - 0.1;
        }
    
        // Calculate the new stability
        const newStability = currentStability * newEasinessFactor;
    
        // Calculate the next review time based on the stability and the time passed since the last review
        const currentTime = new Date().getTime();
        const nextReviewTime = lastReviewedTime.getTime() + newStability * 24 * 60 * 60 * 1000; // Convert days to milliseconds
    
        // Return the time in milliseconds when the next review should happen
        return new Date(nextReviewTime);
      }
    calculateSM18Algorithm(card: LeitnerMessage, quality: number): Date {
        if (quality < 0 || quality > 5) {
            throw new Error("Quality should be within the range 0-5");
        }
    
        let easiness = card.getEasinessFactor();
        let repetitions = card.getRepetitions();
        let stability = card.getStability();
        let interval: number;
    
        // Update easiness factor based on quality
        if (quality < 3) {
            easiness = Math.max(1.3, easiness - 0.2);
        } else {
            easiness = easiness + 0.1 - (5 - quality) * 0.08;
        }
        card.setEasinessFactor(easiness);
    
        // Update repetitions and interval
        if (repetitions === 1) {
            interval = 1;  // First repetition, very short interval
        } else if (repetitions === 2) {
            interval = 6;  // Second repetition, slightly longer interval
        } else {
            stability = stability * (1 + (quality - 3) / 5);
            interval = stability;
        }
        card.setStability(stability);
    
        // Calculate next practice date
        const today = new Date();
        const nextPracticeDate = new Date(today.getTime() + Math.round(interval) * 24 * 60 * 60 * 1000);
        card.setNextReviewDate(nextPracticeDate);
    
        this.logger.debug("Next practice date:", {nextPracticeDate});
        return nextPracticeDate;
    }



}
